import React, { useEffect, useRef, useState } from "react";

interface DeductionDialogProps {
  open: boolean;
  builderName?: string;
  initialValue?: string;
  onCancel: () => void;
  onContinue: (comment: string) => void;
}

const DeductionDialog: React.FC<DeductionDialogProps> = ({
  open,
  builderName,
  initialValue = "",
  onCancel,
  onContinue,
}) => {
  const [comment, setComment] = useState("");
  const continueButtonRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (open) {
      setComment(initialValue || "");
    }
  }, [open, initialValue]);

  if (!open) return null;

  const isValid = comment.trim().length >= 3;

  return (
    <div className="fixed inset-0 z-[70] overflow-y-auto">
      <div className="flex items-end justify-center min-h-screen px-4 pb-20 pt-4 text-center sm:block sm:p-0">
        <div className="fixed inset-0 bg-gray-500 opacity-75" onClick={onCancel} />

        <span className="hidden sm:inline-block sm:h-screen sm:align-middle" aria-hidden="true">
          &#8203;
        </span>

        <div className="inline-block w-full max-w-lg transform overflow-hidden rounded-lg bg-white text-left align-bottom shadow-xl transition-all sm:my-8 sm:align-middle">
          <form
            onSubmit={(e) => {
              e.preventDefault();
              if (isValid) onContinue(comment.trim());
            }}
          >
            <div className="bg-white px-4 pb-4 pt-5 sm:p-6">
              <h3 className="text-lg font-medium text-gray-900">
                Bonus Deduction Explanation
              </h3>

              {builderName && (
                <p className="mt-1 text-sm text-gray-600">
                  Builder: <span className="font-medium">{builderName}</span>
                </p>
              )}

              <p className="mt-3 text-sm text-gray-700">
                Please provide a short explanation for this builder deduction.
              </p>

              <textarea
                autoFocus
                rows={4}
                value={comment}
                onChange={(e) => setComment(e.target.value.slice(0, 500))}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !e.shiftKey) {
                    e.preventDefault();
                    continueButtonRef.current?.focus();
                  }
                }}
                className="mt-4 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-primary-500"
                placeholder="Enter Deduction Explanation"
              />

              <div
                className={`mt-1 text-right text-xs ${
                  comment.length >= 450 ? "text-red-500" : "text-gray-400"
                }`}
              >
                {500 - comment.length} characters remaining
              </div>

              {comment.length > 0 && !isValid && (
                <p className="mt-2 text-xs text-red-600">
                  Please enter at least 3 characters.
                </p>
              )}
            </div>

            <div className="bg-gray-50 px-4 py-3 sm:flex sm:flex-row-reverse sm:px-6">
              <button
                ref={continueButtonRef}
                type="submit"
                disabled={!isValid}
                className="inline-flex w-full justify-center rounded-md border border-transparent bg-blue-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50 sm:ml-3 sm:w-auto"
              >
                Continue
              </button>

              <button
                type="button"
                onClick={onCancel}
                className="mt-3 inline-flex w-full justify-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 sm:mt-0 sm:w-auto"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default DeductionDialog;